/**
 * <uterific-toc> — a sticky table of contents beside a journal post, with a scroll-spy.
 *
 * Reads the post's h2 headings, gives each an id so it can be linked to, lists them in the
 * rail, and as the visitor reads it marks the heading they are in and fills the rail's
 * line to show how far through the post they are. Scroll is captured on the document
 * because above 990px Horizon scrolls .page-wrapper, not the window.
 */

const clamp = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const slug = (text) =>
  text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'section';

class UterificToc extends HTMLElement {
  connectedCallback() {
    const bodySelector = this.getAttribute('for') || '.blog-post-content';
    this.body = document.querySelector(bodySelector);
    this.list = this.querySelector('[data-ut-toc-list]');
    this.fill = this.querySelector('[data-ut-toc-fill]');
    if (!this.body || !this.list) return;

    this.headings = Array.from(this.body.querySelectorAll('h2'));
    if (this.headings.length < 2) {
      this.hidden = true;
      return;
    }

    const used = new Set();
    this.items = this.headings.map((heading) => {
      let id = heading.id || slug(heading.textContent.trim());
      let base = id;
      let n = 2;
      while (used.has(id) || (document.getElementById(id) && document.getElementById(id) !== heading)) {
        id = `${base}-${n++}`;
      }
      used.add(id);
      heading.id = id;

      const li = document.createElement('li');
      li.className = 'ut-toc__item';
      const a = document.createElement('a');
      a.className = 'ut-toc__link';
      a.href = `#${id}`;
      a.textContent = heading.textContent.trim();
      li.appendChild(a);
      this.list.appendChild(li);
      return { heading, li, a };
    });

    this.onScroll = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
    };
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.onScroll);
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  update() {
    this.frame = 0;
    const line = window.innerHeight * 0.35;

    // The current section is the last heading that has passed the reading line.
    let current = -1;
    this.items.forEach(({ heading }, i) => {
      if (heading.getBoundingClientRect().top <= line) current = i;
    });
    this.items.forEach(({ li, a }, i) => {
      const on = i === current;
      li.classList.toggle('is-current', on);
      li.classList.toggle('is-read', i < current);
      if (on) a.setAttribute('aria-current', 'location');
      else a.removeAttribute('aria-current');
    });

    // Progress through the post, from its top reaching the reading line to its end.
    const rect = this.body.getBoundingClientRect();
    const total = rect.height - line;
    const progress = total > 0 ? clamp((line - rect.top) / total) : 1;
    if (this.fill) this.fill.style.setProperty('--ut-progress', progress.toFixed(4));
  }
}

if (!customElements.get('uterific-toc')) {
  customElements.define('uterific-toc', UterificToc);
}
